import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { PlusIcon, PriceTagIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTripBookingPayment } from '../../../api/trip-bookings/create-trip-booking-payment'
import { getTripBooking } from '../../../api/trip-bookings/get-trip-booking'
import { updateTripBookingPayment } from '../../../api/trip-bookings/update-trip-booking-payment'
import { useObserver } from '../../../lib/contexts/observer'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TripBookingPayment } from '../../../lib/types/models/trip-booking/trip-booking-payment'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import TripBookingPaymentForm from './trip-booking-payment-form'

type Props = {
  tripBookingId: number
}
const TripBookingPaymentsDataTable = ({ tripBookingId }: Props) => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    TripBookingPayment
  >(['create', 'edit'])
  const tableRef = React.useRef<DynamicTableRef<TripBookingPayment>>(null)
  const queryClient = useQueryClient()

  useObserver({
    key: 'trip-booking-payments',
    update: () => {
      queryClient.invalidateQueries({ queryKey: ['trip-booking', tripBookingId] })
    }
  })

  const { data: tripBooking } = useQuery({
    queryKey: ['trip-booking', tripBookingId],
    queryFn: () => getTripBooking(tripBookingId, 'PARTIAL')
  })

  const { mutateAsync: createMutation } = useMutation({
    mutationFn: createTripBookingPayment,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Règlement créé avec succès')
    }
  })

  const { mutateAsync: updateMutation } = useMutation({
    mutationFn: updateTripBookingPayment,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Règlement modifié avec succès')
    }
  })

  return (
    <div>
      <NestedDataTable<TripBookingPayment, null>
        ref={tableRef}
        title={'Suivi des règlements'}
        variant="action"
        widget={(data) => {
          const paidAmount = data.reduce((acc, curr) => acc + curr.amount, 0)
          const totalAmount = tripBooking?.isTakeInCharge ? 0 : (tripBooking?.totalPrice ?? 0)
          return (
            <div className="flex items-center gap-2">
              <Badge
                text={`Montant total ${NumberFormatter.formatCurrency(totalAmount)}`}
                variant="warning"
                className="rounded"
              />
              <Badge
                text={`Payé ${NumberFormatter.formatCurrency(paidAmount)}`}
                variant="fushia"
                className="rounded"
              />
              <Badge
                text={`Solde restant ${NumberFormatter.formatCurrency(Math.abs(totalAmount - paidAmount))}`}
                variant="purple"
                className="rounded"
              />
            </div>
          )
        }}
        deleteMessage={{
          name: 'Règlement',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce règlement ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces règlements ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les règlements ?'
        }}
        columns={[
          {
            accessorKey: 'amount',
            header: 'Montant du règlement (€)',
            cell: ({ row }) => <Badge text={row.original.amount.toString()} variant="primary" />
          },
          {
            accessorKey: 'date',
            header: 'Date de paiement',
            cell: ({ row }) => (
              <Badge text={DateFormatter.DDMMYYYY(row.original.date)} variant="grey" />
            )
          }
        ]}
        onEditAction={(payment) => toggleModal('edit', payment)}
        endpoint={API['trip-bookings'].getPayments.url.replace(':id', tripBookingId.toString())}
        actions={[
          {
            icon: PlusIcon,
            variant: 'outline',
            label: 'Ajouter un réglement',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un règlement'}
        icon={PriceTagIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <TripBookingPaymentForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: async (values) => createMutation({ tripBookingId, ...values })
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier un règlement'}
          icon={PriceTagIcon}
          hideActions
          size="medium"
          onCancel={() => closeModel('edit')}
        >
          <TripBookingPaymentForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: async (values) =>
                updateMutation({ id: selectedData.id, tripBookingId, ...values })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default TripBookingPaymentsDataTable
